import { useRef, useState, useEffect } from 'react';

/**
 * Custom hook for handling swipe gestures on touch devices
 * @param {Object} options - Configuration options
 * @param {Function} options.onSwipeLeft - Callback when swiping left
 * @param {Function} options.onSwipeRight - Callback when swiping right
 * @param {Function} options.onSwipeUp - Callback when swiping up
 * @param {Function} options.onSwipeDown - Callback when swiping down
 * @param {number} options.threshold - Minimum distance in pixels to trigger swipe (default: 50)
 * @returns {Object} - Handlers and state for swipe gestures
 */
export const useSwipe = (options = {}) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
  } = options;

  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [swipeDistance, setSwipeDistance] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
    setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    
    setTouchEnd({
      x: currentX,
      y: currentY,
    });

    const deltaX = currentX - touchStart.x;
    const deltaY = currentY - touchStart.y;
    
    // Determine primary direction
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      setSwipeDirection(deltaX > 0 ? 'right' : 'left');
      setSwipeDistance(Math.abs(deltaX));
    } else {
      setSwipeDirection(deltaY > 0 ? 'down' : 'up');
      setSwipeDistance(Math.abs(deltaY));
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;

    const deltaX = touchEnd.x - touchStart.x;
    const deltaY = touchEnd.y - touchStart.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Horizontal swipe
    if (absDeltaX > absDeltaY && absDeltaX > threshold) {
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    }
    // Vertical swipe
    else if (absDeltaY > absDeltaX && absDeltaY > threshold) {
      if (deltaY > 0 && onSwipeDown) {
        onSwipeDown();
      } else if (deltaY < 0 && onSwipeUp) {
        onSwipeUp();
      }
    }

    // Reset state
    setIsSwiping(false);
    setSwipeDirection(null);
    setSwipeDistance(0);
  };

  return {
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isSwiping,
    swipeDirection,
    swipeDistance,
  };
};

/**
 * Custom hook for handling pull-to-refresh gesture
 * @param {Function} onRefresh - Callback when refresh is triggered
 * @param {number} threshold - Minimum pull distance to trigger refresh (default: 80)
 * @returns {Object} - Handlers and state for pull-to-refresh
 */
export const usePullToRefresh = (onRefresh, threshold = 80) => {
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const startX = useRef(0);
  const currentY = useRef(0);
  const hasMoved = useRef(false);

  const handleTouchStart = (e) => {
    // Only activate if scrolled to top
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      startX.current = e.touches[0].clientX;
      hasMoved.current = false;
      // Don't set isPulling yet - wait for actual movement
    }
  };

  const handleTouchMove = (e) => {
    if (isRefreshing) return;

    // Check if we have a valid start position
    if (startY.current === 0) return;

    currentY.current = e.touches[0].clientY;
    const currentX = e.touches[0].clientX;
    const distanceY = currentY.current - startY.current;
    const distanceX = Math.abs(currentX - startX.current);

    // Only activate pull-to-refresh if:
    // 1. Movement is primarily vertical (distanceY > distanceX)
    // 2. Movement is downward (distanceY > 0)
    // 3. Movement exceeds minimum threshold (20px to avoid accidental triggers)
    if (distanceY > 20 && distanceY > distanceX * 2) {
      if (!hasMoved.current) {
        hasMoved.current = true;
        setIsPulling(true);
      }
      
      // Prevent default scroll behavior when pulling down
      e.preventDefault();
      
      // Apply rubber band effect (diminishing returns)
      const rubberBandDistance = Math.min(distanceY * 0.5, threshold * 1.5);
      setPullDistance(rubberBandDistance);
    }
  };

  const handleTouchEnd = async () => {
    // Reset start positions
    startY.current = 0;
    startX.current = 0;
    
    if (!isPulling && !hasMoved.current) {
      // No pull gesture detected, allow normal click/tap
      return;
    }

    if (pullDistance >= threshold) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh error:', error);
      } finally {
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 500);
      }
    } else {
      setPullDistance(0);
    }

    setIsPulling(false);
    hasMoved.current = false;
  };

  return {
    containerRef,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isPulling,
    isRefreshing,
    pullDistance,
  };
};

/**
 * Custom hook for swipeable cards with action reveal
 * @param {Object} options - Configuration options
 * @returns {Object} - State and handlers for swipeable card
 */
export const useSwipeableCard = (options = {}) => {
  const { threshold = 70, maxSwipe = 180 } = options;
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const touchStartX = useRef(0);
  const currentTouchX = useRef(0);
  const isSwiping = useRef(false);

  const handleTouchStart = (e) => {
    // Stop propagation to prevent pull-to-refresh interference
    e.stopPropagation();
    touchStartX.current = e.touches[0].clientX;
    isSwiping.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isSwiping.current) return;

    // Stop propagation to prevent pull-to-refresh interference
    e.stopPropagation();
    
    currentTouchX.current = e.touches[0].clientX;
    const diff = touchStartX.current - currentTouchX.current;

    // Only allow swipe left (to reveal actions on right)
    if (diff > 0) {
      const offset = Math.min(diff, maxSwipe);
      setSwipeOffset(offset);
      // Prevent default to stop scrolling while swiping
      if (offset > 10) {
        e.preventDefault();
      }
    } else if (isOpen) {
      // Allow swipe right to close
      const offset = Math.max(maxSwipe + diff, 0);
      setSwipeOffset(offset);
    }
  };

  const handleTouchEnd = (e) => {
    // Stop propagation to prevent pull-to-refresh interference
    e.stopPropagation();
    isSwiping.current = false;

    if (swipeOffset > threshold) {
      setSwipeOffset(maxSwipe);
      setIsOpen(true);
    } else {
      setSwipeOffset(0);
      setIsOpen(false);
    }
  };

  const close = () => {
    setSwipeOffset(0);
    setIsOpen(false);
  };

  return {
    swipeOffset,
    isOpen,
    isSwiping: isSwiping.current,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    close,
  };
};

export default useSwipe;
